/**
 * Universal Order Calculation Utilities
 * 
 * This module provides consistent calculation functions for orders across all components
 * to ensure accuracy and prevent discrepancies between different views.
 */

/**
 * Calculate item subtotal (base amount before discounts and hidden fees)
 * @param {Object} item - Order item
 * @returns {number} Subtotal amount
 */
export const calculateItemSubtotal = (item) => {
  if (!item) return 0;
  
  const quantity = parseFloat(item.quantity || 1);
  const unitPrice = parseFloat(item.unit_price || 0);
  
  let subtotal = 0;
  
  // Calculate based on whether item has dimensions
  if (item.width && item.height && 
      !isNaN(parseFloat(item.width)) && !isNaN(parseFloat(item.height))) {
    // Dimensional item: area × unit_price × quantity
    const area = parseFloat(item.width) * parseFloat(item.height);
    subtotal = area * unitPrice * quantity;
  } else {
    // Non-dimensional item: unit_price × quantity
    subtotal = unitPrice * quantity;
  }
  
  return subtotal;
};

/**
 * Calculate item subtotal including hidden fees
 * @param {Object} item - Order item
 * @returns {number} Subtotal with hidden fees
 */
export const calculateItemSubtotalWithHiddenFees = (item) => {
  if (!item) return 0;
  
  const baseSubtotal = calculateItemSubtotal(item);
  const hiddenFees = parseFloat(item.hidden_fees || 0);
  
  return baseSubtotal + hiddenFees;
};

/**
 * Calculate item discount amount
 * @param {Object} item - Order item
 * @returns {number} Discount amount
 */
export const calculateItemDiscount = (item) => {
  if (!item) return 0;
  
  // If discount_amount is already stored and correct, use it
  let discountAmount = parseFloat(item.discount_amount || 0);
  
  // For manual calculation or validation
  if (item.discount_type && item.discount_value > 0) {
    const quantity = parseFloat(item.quantity || 1);
    const baseSubtotal = calculateItemSubtotal(item);
    
    if (item.discount_type === 'percentage') {
      // Percentage discount applies to the base subtotal
      discountAmount = (baseSubtotal * parseFloat(item.discount_value)) / 100;
    } else if (item.discount_type === 'fixed') {
      // Fixed discount applies per quantity
      discountAmount = parseFloat(item.discount_value) * quantity;
    }
  }
  
  return discountAmount;
};

/**
 * Calculate final item total (subtotal + hidden fees - discount)
 * @param {Object} item - Order item
 * @returns {number} Final total
 */
export const calculateItemTotal = (item) => {
  if (!item) return 0;
  
  const subtotalWithHiddenFees = calculateItemSubtotalWithHiddenFees(item);
  const discountAmount = calculateItemDiscount(item);
  
  return Math.max(0, subtotalWithHiddenFees - discountAmount);
};

/**
 * Calculate adjusted unit price (includes hidden fees, for customer-facing documents)
 * @param {Object} item - Order item
 * @returns {number} Adjusted unit price per quantity
 */
export const calculateAdjustedUnitPrice = (item) => {
  if (!item) return 0;
  
  const quantity = parseFloat(item.quantity || 1);
  if (quantity === 0) return 0;
  
  const subtotalWithHiddenFees = calculateItemSubtotalWithHiddenFees(item);
  return subtotalWithHiddenFees / quantity;
};

/**
 * Calculate discount per unit (discount amount divided by quantity)
 * @param {Object} item - Order item
 * @returns {number} Discount per unit
 */
export const calculateDiscountPerUnit = (item) => {
  if (!item) return 0;
  
  const quantity = parseFloat(item.quantity || 1);
  if (quantity === 0) return 0;
  
  const discountAmount = calculateItemDiscount(item);
  return discountAmount / quantity;
};

/**
 * Calculate net unit price (unit price after discount per unit)
 * This is the unit price minus the discount per unit
 * @param {Object} item - Order item
 * @returns {number} Net unit price (unit price - discount per unit)
 */
export const calculateNetUnitPrice = (item) => {
  if (!item) return 0;
  
  const quantity = parseFloat(item.quantity || 1);
  if (quantity === 0) return 0;
  
  const adjustedUnitPrice = calculateAdjustedUnitPrice(item);
  const discountPerUnit = calculateDiscountPerUnit(item);
  
  // Net unit price = unit price - discount per unit
  return adjustedUnitPrice - discountPerUnit;
};

/**
 * Calculate order totals from items array
 * @param {Array} items - Array of order items
 * @param {number} orderDiscount - Order-level discount amount
 * @returns {Object} Order totals
 */
export const calculateOrderTotals = (items = [], orderDiscount = 0) => {
  if (!Array.isArray(items)) return { subtotal: 0, totalDiscount: 0, total: 0 };
  
  let subtotal = 0;
  let itemDiscounts = 0;
  let hiddenFees = 0;
  
  items.forEach(item => {
    subtotal += calculateItemSubtotal(item);
    itemDiscounts += calculateItemDiscount(item);
    hiddenFees += parseFloat(item.hidden_fees || 0);
  });
  
  const subtotalWithHiddenFees = subtotal + hiddenFees;
  const totalDiscounts = itemDiscounts + parseFloat(orderDiscount || 0);
  const total = subtotalWithHiddenFees - totalDiscounts;
  
  return {
    subtotal: subtotal, // Original subtotal without hidden fees
    subtotalWithHiddenFees: subtotalWithHiddenFees, // Subtotal including hidden fees
    hiddenFees: hiddenFees,
    itemDiscounts: itemDiscounts,
    orderDiscount: parseFloat(orderDiscount || 0),
    totalDiscounts: totalDiscounts,
    total: Math.max(0, total)
  };
};

/**
 * Calculate order discount percentage
 * @param {number} orderDiscount - Order discount amount
 * @param {number} subtotal - Order subtotal (basis for percentage calculation)
 * @returns {string} Percentage with 2 decimal places
 */
export const calculateOrderDiscountPercentage = (orderDiscount, subtotal) => {
  if (!subtotal || subtotal <= 0) return '0.00';
  return ((orderDiscount / subtotal) * 100).toFixed(2);
};

/**
 * Format price for display
 * @param {number} price - Price to format
 * @returns {string} Formatted price
 */
export const formatPrice = (price) => {
  return Number(price || 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Validate item calculations (for debugging)
 * @param {Object} item - Order item
 * @returns {Object} Calculation breakdown
 */
export const validateItemCalculations = (item) => {
  if (!item) return null;
  
  return {
    product: item.product_name || 'Unknown',
    quantity: item.quantity,
    unitPrice: item.unit_price,
    width: item.width,
    height: item.height,
    area: item.width && item.height ? item.width * item.height : null,
    baseSubtotal: calculateItemSubtotal(item),
    hiddenFees: item.hidden_fees || 0,
    subtotalWithHiddenFees: calculateItemSubtotalWithHiddenFees(item),
    discountType: item.discount_type,
    discountValue: item.discount_value,
    discountAmount: calculateItemDiscount(item),
    finalTotal: calculateItemTotal(item),
    adjustedUnitPrice: calculateAdjustedUnitPrice(item)
  };
};
